import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { createUser, updateUser } from '../../services/adminService';
import { User, UserType, CreateUserRequest, UpdateUserRequest } from '../../types/admin';
import './UserForm.css';

interface UserFormProps {
	user?: User | null;
	isOpen: boolean;
	onClose: () => void;
	onSuccess: () => void;
	mode: 'create' | 'edit';
}

export const UserForm: React.FC<UserFormProps> = ({ user, isOpen, onClose, onSuccess, mode }) => {
	const { token } = useAuth();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string>('');

	const [formData, setFormData] = useState({
		firstName: '',
		lastName: '',
		phoneNumber: '',
		email: '',
		type: UserType.SOCIAL,
		institution: '',
		isActive: true,
	});

	const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

	useEffect(() => {
		if (mode === 'edit' && user) {
			setFormData({
				firstName: user.firstName,
				lastName: user.lastName,
				phoneNumber: user.phoneNumber,
				email: user.email,
				type: user.type,
				institution: user.institution,
				isActive: user.isActive,
			});
		} else {
			setFormData({
				firstName: '',
				lastName: '',
				phoneNumber: '',
				email: '',
				type: UserType.SOCIAL,
				institution: '',
				isActive: true,
			});
		}
		setError('');
		setValidationErrors({});
	}, [mode, user, isOpen]);

	const validateForm = (): boolean => {
		const errors: Record<string, string> = {};

		if (!formData.firstName.trim()) {
			errors.firstName = 'Ime je obavezno';
		}

		if (!formData.lastName.trim()) {
			errors.lastName = 'Prezime je obavezno';
		}

		if (!formData.email.trim()) {
			errors.email = 'Email je obavezan';
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
			errors.email = 'Email format nije valjan';
		}

		if (!formData.phoneNumber.trim()) {
			errors.phoneNumber = 'Telefon je obavezan';
		}

		setValidationErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validateForm() || !token) return;

		try {
			setLoading(true);
			setError('');

			if (mode === 'create') {
				const createData: CreateUserRequest = {
					firstName: formData.firstName.trim(),
					lastName: formData.lastName.trim(),
					phoneNumber: formData.phoneNumber.trim(),
					email: formData.email.trim(),
					type: formData.type,
					institution: formData.institution.trim(),
				};

				await createUser(token, createData);
			} else if (mode === 'edit' && user) {
				const updateData: UpdateUserRequest = {
					firstName: formData.firstName.trim(),
					lastName: formData.lastName.trim(),
					phoneNumber: formData.phoneNumber.trim(),
					email: formData.email.trim(),
					type: formData.type,
					institution: formData.institution.trim(),
					isActive: formData.isActive,
				};

				await updateUser(token, user.id, updateData);
			}

			onSuccess();
			onClose();
		} catch (err: any) {
			setError(err.message || 'Greška pri čuvanju korisnika');
		} finally {
			setLoading(false);
		}
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		const { name, value, type } = e.target;
		const checked = (e.target as HTMLInputElement).checked;

		setFormData((prev) => ({
			...prev,
			[name]: type === 'checkbox' ? checked : value,
		}));

		// Clear validation error when user starts typing
		if (validationErrors[name]) {
			setValidationErrors((prev) => ({
				...prev,
				[name]: '',
			}));
		}
	};

	const getUserTypeLabel = (type: UserType) => {
		switch (type) {
			case UserType.ADMIN:
				return 'Administrator';
			case UserType.SOCIAL:
				return 'Socijalni radnik';
			case UserType.HEALTH:
				return 'Zdravstveni radnik';
			default:
				return type;
		}
	};

	if (!isOpen) return null;

	return (
		<div className='modal-overlay'>
			<div className='modal-content user-form-modal'>
				<div className='modal-header'>
					<h3>{mode === 'create' ? 'Dodaj novog korisnika' : 'Uredi korisnika'}</h3>
					<button className='close-btn' onClick={onClose} disabled={loading}>
						×
					</button>
				</div>

				<form onSubmit={handleSubmit} className='user-form'>
					{error && <div className='error-message'>{error}</div>}

					<div className='form-row'>
						<div className='form-group'>
							<label htmlFor='firstName'>Ime *</label>
							<input type='text' id='firstName' name='firstName' value={formData.firstName} onChange={handleChange} className={validationErrors.firstName ? 'error' : ''} disabled={loading} placeholder='Unesite ime korisnika' />
							{validationErrors.firstName && <span className='field-error'>{validationErrors.firstName}</span>}
						</div>

						<div className='form-group'>
							<label htmlFor='lastName'>Prezime *</label>
							<input type='text' id='lastName' name='lastName' value={formData.lastName} onChange={handleChange} className={validationErrors.lastName ? 'error' : ''} disabled={loading} placeholder='Unesite prezime korisnika' />
							{validationErrors.lastName && <span className='field-error'>{validationErrors.lastName}</span>}
						</div>
					</div>

					<div className='form-row'>
						<div className='form-group'>
							<label htmlFor='email'>Email *</label>
							<input type='email' id='email' name='email' value={formData.email} onChange={handleChange} className={validationErrors.email ? 'error' : ''} disabled={loading} placeholder='Unesite email korisnika' />
							{validationErrors.email && <span className='field-error'>{validationErrors.email}</span>}
						</div>

						<div className='form-group'>
							<label htmlFor='phoneNumber'>Telefon *</label>
							<input type='tel' id='phoneNumber' name='phoneNumber' value={formData.phoneNumber} onChange={handleChange} placeholder='+381...' className={validationErrors.phoneNumber ? 'error' : ''} disabled={loading} />
							{validationErrors.phoneNumber && <span className='field-error'>{validationErrors.phoneNumber}</span>}
						</div>
					</div>

					<div className='form-row'>
						<div className='form-group'>
							<label htmlFor='type'>Tip korisnika *</label>
							<select id='type' name='type' value={formData.type} onChange={handleChange} disabled={loading}>
								<option value={UserType.SOCIAL}>{getUserTypeLabel(UserType.SOCIAL)}</option>
								<option value={UserType.HEALTH}>{getUserTypeLabel(UserType.HEALTH)}</option>
								<option value={UserType.ADMIN}>{getUserTypeLabel(UserType.ADMIN)}</option>
							</select>
						</div>

						<div className='form-group'>
							<label htmlFor='institution'>Ustanova</label>
							<input type='text' id='institution' name='institution' value={formData.institution} onChange={handleChange} className={validationErrors.institution ? 'error' : ''} disabled={loading} placeholder='Unesite naziv ustanove' />
							{/* {validationErrors.institution && <span className='field-error'>{validationErrors.institution}</span>} */}
						</div>
					</div>

					{/* Status checkbox for edit mode */}
					{mode === 'edit' && (
						<div className='form-row'>
							<div className='form-group checkbox-group'>
								<label htmlFor='isActive' className='checkbox-label'>
									<input type='checkbox' id='isActive' name='isActive' checked={formData.isActive} onChange={handleChange} disabled={loading} />
									Korisnik je aktivan
								</label>
							</div>
						</div>
					)}

					<div className='form-actions'>
						<button type='button' className='btn btn-outline' onClick={onClose} disabled={loading}>
							Otkaži
						</button>
						<button type='submit' className='btn btn-primary' disabled={loading}>
							{loading ? 'Čuvanje...' : mode === 'create' ? 'Kreiraj' : 'Sačuvaj'}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};
